// src/pages/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE from '../config';
import './LandingPage.css';


function LandingPage() {
  const navigate = useNavigate();

  // Genus of the day
  const [genusData, setGenusData] = useState(null);
  const [genusLoading, setGenusLoading] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/get_genus_of_the_day`)
      .then(r => r.json())
      .then(d => { if (d.slides?.length) setGenusData(d); })
      .catch(() => {})
      .finally(() => setGenusLoading(false));
  }, []);

  // Auto-advance every 5 s; restarting on manual nav resets the timer
  useEffect(() => {
    if (!genusData) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSlideIndex(i => (i + 1) % genusData.slides.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [genusData, slideIndex]);

  const handleStart = async () => {
    try {
      // 1. Start a new game session
      const response = await fetch(API_BASE + '/start_game', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      console.log('Game initialized:', result);

      const sessionId = result.session_id;
      if (!sessionId) {
        console.error('No session_id returned from /start_game');
        return;
      }

      // 2. Clear any old images from previous sessions
      try {
        const clearRes = await fetch(API_BASE + '/clear_images?session_id=' + sessionId);
        if (!clearRes.ok) {
          console.error('Failed to clear images:', await clearRes.text());
        }
      } catch (err) {
        console.error('Error calling /clear_images:', err);
        // Not fatal; still try to continue
      }

      // 3. Go to settings page
      navigate('/settings?session=' + sessionId);
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };

  const prevSlide = () => setSlideIndex(i => (i - 1 + genusData.slides.length) % genusData.slides.length);
  const nextSlide = () => setSlideIndex(i => (i + 1) % genusData.slides.length);

  return (
    <div>
      <div className="page-header">
        <button className="page-title-btn" onClick={() => navigate('/')}>
          <img src="/logo.png?v=2" alt="" className="header-logo" />
          PlantID Game
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <h1>Welcome to the Plant Identification Game</h1>

        <button onClick={handleStart} style={{ padding: '11.5px 23px', fontSize: '18.4px', marginTop: '24px', marginBottom: '24px', border: '2px solid var(--moss-dark)' }}>
          Start
        </button>

        {genusLoading && (
          <p className="gotd-loading">Loading genus of the day…</p>
        )}

        {genusData && genusData.slides.length > 0 && (() => {
          const slide = genusData.slides[slideIndex];
          return (
            <div className="gotd-outer">
              <div className="gotd-description gotd-description-left">
                <h3 className="gotd-side-heading">About the game</h3>
                <p>This game was created to help you practice your plant identification skills.</p>
                <p>Create a game based on your needs, by selecting genera or countries you are interested in. Based on your settings the game will create a unique round every time you play, by sampling from all relevant iNaturalist observations. During the game you will see one photo in each round and your task is to correctly guess the genus or even the species depending on your settings. Throughout the game you will be able to see interesting metadata regarding the different genera.</p>
                <p>Afterwards you can see a summary of your performance.</p>
              </div>
              <div className="gotd-description gotd-description-right">
                <h3 className="gotd-side-heading">About the origin</h3>
                <p>We are two students from Germany with a strong interest in all types of plants.</p>
                <p>Because we had the need for a highly customizable identification training game we developed the idea to create our own game. Based on this motivation the Plant ID Game grew over the past year.</p>
                <p>At one point we came to the conclusion that it maybe would also provide value to other plant enthusiasts, so we decided to make it public. The version you see right now is still in active development and we have lots of ideas for future features and improvements.</p>
                <p>We hope that you enjoy the game and would be thankful to receive your <Link to="/about" className="gotd-donation-link">feedback</Link>.</p>
              </div>
            <div className="gotd-wrapper">
              <h2 className="gotd-title">Genus of the day: <em>{genusData.genus}</em></h2>
              <div className="gotd-slideshow">
                <button className="gotd-nav" onClick={prevSlide}>&#8249;</button>
                <div className="gotd-viewport">
                  <div className="gotd-track" style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
                    {genusData.slides.map((s, i) => (
                      <div key={i} className="gotd-track-item">
                        <img src={s.image_url} alt={s.scientific_name} className="gotd-img" />
                      </div>
                    ))}
                  </div>
                </div>
                <button className="gotd-nav" onClick={nextSlide}>&#8250;</button>
              </div>
              <div className="gotd-caption">
                {slide.observation_link
                  ? <a href={slide.observation_link} target="_blank" rel="noopener noreferrer" className="gotd-scientific">{slide.scientific_name}</a>
                  : <span className="gotd-scientific">{slide.scientific_name}</span>
                }
                {slide.author && (
                  <span className="gotd-author">
                    photo by {slide.author}
                    {slide.license_code && (() => {
                      const lcMap = {
                        'cc0':         { label: 'CC0 (Public Domain)', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
                        'cc-by':       { label: 'CC BY',               url: 'https://creativecommons.org/licenses/by/4.0/' },
                        'cc-by-nc':    { label: 'CC BY-NC',            url: 'https://creativecommons.org/licenses/by-nc/4.0/' },
                        'cc-by-sa':    { label: 'CC BY-SA',            url: 'https://creativecommons.org/licenses/by-sa/4.0/' },
                        'cc-by-nc-sa': { label: 'CC BY-NC-SA',         url: 'https://creativecommons.org/licenses/by-nc-sa/4.0/' },
                      };
                      const info = lcMap[slide.license_code.toLowerCase()];
                      if (!info) return null;
                      return (
                        <> · <a href={info.url} target="_blank" rel="noopener noreferrer" className="gotd-license">{info.label}</a></>
                      );
                    })()}
                  </span>
                )}
              </div>
              <div className="gotd-dots">
                {genusData.slides.map((_, i) => (
                  <span
                    key={i}
                    className={`gotd-dot${i === slideIndex ? ' active' : ''}`}
                    onClick={() => setSlideIndex(i)}
                  />
                ))}
              </div>
            </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}

export default LandingPage;
